import React, { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator, Pressable } from "react-native";
import { useNavigate } from "react-router-dom";
import Ionicons from "react-native-vector-icons/Ionicons";
import { NormalText } from "../../../common/native";
import { getPatients, TPatientInfo } from "../../../../services/api-clinic";
import { TClinicPatientType, TSession } from "../../../../utils/types";

type NotificationsSectionProps = {
  session: TSession;
};

const NotificationsSection: React.FC<NotificationsSectionProps> = ({
  session,
}) => {
  const [leadNotifications, setLeadNotifications] = useState<TPatientInfo[]>(
    [],
  );
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetchRecentLeads = async () => {
      setIsLoadingNotifications(true);

      try {
        const leads = await getPatients(session, [TClinicPatientType.LEAD]);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);

        const recentLeads = leads
          .filter((lead) => {
            if (!lead.createdAt) return false;
            const createdAt = new Date(lead.createdAt);
            return !Number.isNaN(createdAt.getTime()) && createdAt >= weekAgo;
          })
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

        if (isMounted) setLeadNotifications(recentLeads);
      } catch (err) {
        console.error("Failed to fetch recent leads", err);
        if (isMounted) setLeadNotifications([]);
      } finally {
        if (isMounted) setIsLoadingNotifications(false);
      }
    };

    fetchRecentLeads();
    return () => {
      isMounted = false;
    };
  }, [session]);

  const formatLeadName = (lead: TPatientInfo) => {
    const name = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
    return name || lead.email || lead.phone || "Unnamed lead";
  };

  const formatLeadDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Unknown date";
    return date.toLocaleDateString();
  };

  return (
    <View style={styles.notificationsContainer}>
      {/* <View style={styles.notificationsHeader}>
        <NormalText
          text="Notifications"
          fontWeight="700"
          style={styles.notificationsTitle}
        />
        <NormalText
          text="Past 7 days"
          fontWeight="500"
          style={styles.notificationsSubtitle}
        />
      </View> */}
      {isLoadingNotifications ? (
        <View style={styles.notificationsLoading}>
          <ActivityIndicator size="small" color="#00B7F4" />
        </View>
      ) : leadNotifications.length === 0 ? (
        <View style={styles.notificationsEmpty}>
          <View style={styles.notificationsEmptyIcon}>
            <Ionicons name="notifications-outline" size={22} color="#2563EB" />
          </View>
          <NormalText
            text="You're all caught up"
            fontWeight="700"
            style={styles.notificationsEmptyTitle}
          />
          <NormalText
            text="New leads from the past 7 days will show up here as they come in."
            fontWeight="500"
            style={styles.notificationsEmptySubtitle}
          />
        </View>
      ) : (
        <View style={styles.notificationsGrid}>
          {leadNotifications.map((lead) => (
            <Pressable
              key={lead.uuid}
              onPress={() =>
                navigate(`/patients?patientId=${lead.uuid}&filter=leads`)
              }
              style={({ pressed }) => [
                styles.notificationCard,
                pressed && styles.notificationCardPressed,
              ]}
            >
              <NormalText
                text="New lead"
                fontWeight="600"
                style={styles.notificationLabel}
              />
              <NormalText
                text={formatLeadName(lead)}
                fontWeight="700"
                style={styles.notificationName}
                numberOfLines={1}
              />
              <NormalText
                text={formatLeadDate(lead.createdAt)}
                fontWeight="500"
                style={styles.notificationDate}
              />
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  notificationsContainer: {
    width: "100%",
    maxWidth: 1200,
    marginBottom: 24,
  },
  notificationsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  notificationsTitle: {
    fontSize: 20,
    color: "#0F172A",
  },
  notificationsSubtitle: {
    fontSize: 14,
    color: "#64748B",
  },
  notificationsLoading: {
    paddingVertical: 12,
  },
  notificationsEmpty: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 28,
    paddingHorizontal: 24,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderStyle: "dashed",
    backgroundColor: "#F8FAFC",
  },
  notificationsEmptyIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#EFF6FF",
    marginBottom: 12,
  },
  notificationsEmptyTitle: {
    fontSize: 16,
    color: "#0F172A",
    marginBottom: 4,
  },
  notificationsEmptySubtitle: {
    fontSize: 13,
    color: "#64748B",
    textAlign: "center",
  },
  notificationsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "center",
  },
  notificationCard: {
    flexBasis: 280,
    flexGrow: 1,
    minWidth: 240,
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    backgroundColor: "#F8FAFC",
    shadowColor: "#0F172A",
    shadowOpacity: 0.06,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 2,
  },
  notificationCardPressed: {
    transform: [{ scale: 0.98 }],
    shadowOpacity: 0.1,
  },
  notificationLabel: {
    fontSize: 12,
    color: "#2563EB",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  notificationName: {
    fontSize: 16,
    color: "#0F172A",
    marginBottom: 4,
  },
  notificationDate: {
    fontSize: 13,
    color: "#475569",
  },
});

export default NotificationsSection;
